import React, { useImperativeHandle, forwardRef, useRef, useState, useEffect } from 'react';
import { useNotification } from '../../../hooks/useNotification';
import { useErrorInputs } from '../../../hooks/useErrorInputs';
import { useDataAuth } from '../../../hooks/useDataAuth';
import { useForm } from '../../../hooks/useForm';
import { useTranslation } from "react-i18next";
import ContactsIcon from '@mui/icons-material/Contacts';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import { DividerGradient } from '../../dividerGradient/DividerGradient';
import { GenericAutocomplete } from '../../others/GenericAutocomplete';
import { CreateFormClient } from '../../client/CreateFormClient';
import { LocationClient } from '../../client/LocationClient';
import { ActionButtons } from '../../others/ActionButtons';
import { fetchClientsConcat, fetchOffices, insertDataClient, updateClient, fetchClientById, fetchClientOfficeId } from '../../../services/client.service';

import { Dialog } from '../../others/Dialog';

const initialData = {
    id_company: "",
    type_identification: '',
    identification: '',
    last_name: '',
    identification_digit: '',
    business_name: '',
    supplier_tax_scheme: '',
    supplier_tax_level_code: [],
    location: [],
    contacts:[],
    category: '',
    segment: '',
    additional_fields: ''
}
export const ClientForm = forwardRef(({ dto, handleDirectChange, handleFieldChange, isEdit }, ref) => {
    const createClientRef = useRef();
    const locationRef = useRef();
    const fieldsRequired = ['id_company_receiver'];
    const [errorInputs, , resetErrors, validForm] = useErrorInputs({});
    const [sendNotification] = useNotification();
    const [clients, setClients] = useState([]);
    const [offices, setOffices] = useState([]);
    const [idClient, setIdClient] = useState(null);
    const [openCreateClient, setOpenCreateClient] = useState(false);
    const [{ userSession }] = useDataAuth();
    const { t } = useTranslation();
    const [clientData, handleInputChange, handleDirectInputChange, setAllInfo] = useForm(initialData);

    useImperativeHandle(ref, () => ({
        getData: () => dto,
        errorInputs,
        validForm: () => validForm(fieldsRequired, dto)
    }));

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetchClientsConcat(userSession.token);
                if (!response?.data) return;
                setClients(response.data);
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };
        fetchData();
        // if(dto.id_office_receiver !== ''){
        //     console.log("resetErrors", dto.id_office_receiver)
        //     resetErrors();
        // }
    }, [openCreateClient, isEdit, userSession.token])

    useEffect(() => {
        if (dto?.id_client) {
            handleFetchOffices(dto.id_client);
        }
    }, [dto?.id_client]);

    useEffect(() => {
        if (offices.length === 1) {
            handleDirectChange('id_office_receiver', offices[0].code);
        }
    }, [offices]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if(name === 'id_company_receiver'){
            const client = clients.find(c => c.code === value);
            handleDirectChange('id_company_receiver', client?.code || '');
            if(client === undefined){ 
                setAllInfo(initialData);
                setIdClient(null);
            };
            setIdClient(`${client?.id_client}` || null);
            handleFetchOffices(client?.id_client || '')
            return;
        }
        handleDirectChange(name, value);
    };
    const handleCreateClient = () => {
        setOpenCreateClient(true);
    }
    const handleFetchOffices = async (idClient) => {
        if(idClient.length === 0) {
            handleDirectChange('id_office_receiver', '');
            return;
        };
        const response = await fetchOffices(idClient, userSession.token);
        if (!Array.isArray(response?.data) || response.data.length === 0){
            handleDirectChange('id_office_receiver', '');
            setOffices([]); 
            return
        };
        setOffices(response.data);
        if(response.data.length === 1) {
            handleDirectChange('id_office_receiver', response.data[0].code);
        }
    }

    const Cancel = () => setOpenCreateClient(false);
    const handleSubmit = async (event) => {
        event.preventDefault();
        const isValidCreateClientRef = createClientRef.current?.validateForm();
        const data = JSON.stringify(clientData);
        if (isValidCreateClientRef) {
            if(idClient !== null) {
                await updateClient(data, userSession.token);
                sendNotification({ msg: "Cliente actualizado exitosamente.", variant: "success"});
                Cancel();
            } else {
                const response = await insertDataClient(data, userSession.token);
                sendNotification({ msg: "Cliente creado exitosamente.", variant: "success", });
                Cancel();
                if(response?.data){
                    const dto = response.data;
                    handleDirectChange('id_company_receiver', clientData.identification);
                    handleFetchOffices(dto.id);
                }
            }
        } else {
            sendNotification({
                msg: "Por favor complete todos los campos requeridos",
                variant: "error",
            });
        }
    };

    useEffect(() => {
        const fetchClientData = async () => {
            try {
                if (idClient) {
                    const response_location = await fetchClientOfficeId(userSession.token, idClient);
                    const response = await fetchClientById(userSession.token, idClient);
                    if (response.status === 200 && response.data) {
                        const clientDto = response.data;
                        clientDto.id_client = idClient;
                        clientDto.location = response_location.status === 200 ? response_location.data || [] : [];
                        clientDto.contacts = clientDto.contacts ? clientDto.contacts.split(',').filter(c => c) : [];
                        setAllInfo(clientDto);
                    }
                }
            } catch (e) {
                console.error("Error fetching data ", e);
            }
        };

        if (openCreateClient) {
            fetchClientData();
        }
    }, [openCreateClient, idClient, userSession.token]);

  return (
    <Box sx={{ width: "100%", mx: "auto", mt: 3, mb: 2 }}>
        <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
                <GenericAutocomplete
                    options={clients}
                    value={dto.id_company_receiver}
                    label={t('commonFields.nit')}
                    name="id_company_receiver"
                    required={true}
                    onChange={handleChange}
                    error={Boolean(errorInputs.id_company_receiver?.isError)}
                    helperText={errorInputs.id_company_receiver?.messageError}
                />
            </Grid>
            <Grid size={{ xs:12, sm:1 }}  sx={{ display: 'flex', justifyContent: 'flex-start', padding: 0 }}>
                <Tooltip title={t("add")} placement="top" arrow>
                    <IconButton
                        aria-label="add"
                        onClick={handleCreateClient}
                    >
                        <ContactsIcon color="primary" />
                    </IconButton>
                </Tooltip>
            </Grid>
            <Grid size={{ xs:12, sm:5 }} />
            <Grid size={{ xs: 12, sm: 6 }}>
                <GenericAutocomplete
                    options={offices}
                    value={dto.id_office_receiver}
                    label={t('commonFields.office')}
                    name="id_office_receiver"
                    onChange={handleChange}
                />
            </Grid>
            {/* {dto.id_office_receiver === '' && ( */}
            <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth size="small" label={t('commonFields.location.addressDelivery')} name="address_delivery" 
                    value={dto.address_delivery} 
                    onChange={handleFieldChange} 
                    // error={Boolean(errorInputs.address_delivery?.isError)}
                    // helperText={errorInputs.address_delivery?.messageError}
                />
            </Grid>
            {/* )} */}
            
            <Dialog 
                openDialog={openCreateClient}
                widthDialog={"md"}
                content={
                    <>
                        <CreateFormClient 
                            ref={createClientRef}
                            dto={clientData}
                            handleInputChange={handleInputChange}
                            handleDirectInputChange={handleDirectInputChange}
                        />
                        <Grid size={{xs:12}} sx={{ mb: 3 }} />
                        <DividerGradient/>
                        <LocationClient
                            ref={locationRef}
                            dto={clientData}
                            handleDirectInputChange={handleDirectInputChange}
                        />
                    </>
                }
                actions={
                    <>
                        <ActionButtons
                            onCancel={Cancel}
                            onSubmit={handleSubmit}
                            // disabledSubmit={isView}
                        />
                    </>
                }
            />
            
        </Grid>
    </Box>
  )
})
